import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { debugConsole } from 'backend-client-web';
import { debugAccountEmails } from 'client-core';
import { DebugVisibleContextProvider, useDebugTrigger } from 'client-web-sdk';

import { useDebugVisible } from './localStorage/useLocalStorageState';

export { useDebugVisibleContext } from 'client-web-sdk';

const isProduction = import.meta.env.MODE === 'production';

export function WebDebugVisibleContextProvider({ children }: React.PropsWithChildren<{}>) {
  const [debugVisible, setDebugVisible] = useDebugVisible(!isProduction);

  const onDebugTriggered = useCallback(() => {
    setDebugVisible(!debugVisible);
  }, [debugVisible, setDebugVisible]);

  useDebugTrigger({ onDebugTriggered, touchTrigger: true });

  const history = useHistory();
  useEffect(() => {
    const search = new URLSearchParams(history.location.search);
    if (search.has('DEBUG')) {
      setDebugVisible(true);
      search.delete('DEBUG');
      history.replace({ search: `?${search.toString()}` });
    }
  }, [history, setDebugVisible]);

  useInsertDebugAccountEmail(debugVisible);

  useEffect(() => {
    debugConsole.showDebugMessages = debugVisible;
  }, [debugVisible]);

  return <DebugVisibleContextProvider value={debugVisible}>{children}</DebugVisibleContextProvider>;
}

function useInsertDebugAccountEmail(debugVisible: boolean) {
  useEffect(() => {
    if (!debugVisible) {
      return;
    }
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || !e.altKey) {
        return;
      }
      const { target } = e;
      if (!(target instanceof HTMLInputElement)) {
        return;
      }
      const { value: searchString } = target;
      const debugAccountEmailsList: string[] = Object.values(debugAccountEmails);
      const exactMatchIndex = debugAccountEmailsList.indexOf(searchString);
      const matchingDebugAccountEmail =
        exactMatchIndex !== -1 ?
          debugAccountEmailsList[(exactMatchIndex + 1) % debugAccountEmailsList.length]
        : debugAccountEmailsList.find(
            (email) => email.startsWith(searchString) && email !== searchString,
          );
      if (matchingDebugAccountEmail) {
        e.preventDefault();
        e.stopPropagation();
        setNativeValue(target, matchingDebugAccountEmail);
        target.select();
      }
    };
    window.addEventListener('keydown', onKeydown, true);
    return () => window.removeEventListener('keydown', onKeydown, true);
  }, [debugVisible]);
}

// https://github.com/facebook/react/issues/10135#issuecomment-401496776
function setNativeValue(element: HTMLInputElement, value: string) {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { set: valueSetter } = Object.getOwnPropertyDescriptor(element, 'value') || {};
  const prototype = Object.getPrototypeOf(element);
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { set: prototypeValueSetter } = Object.getOwnPropertyDescriptor(prototype, 'value') || {};
  if (prototypeValueSetter && valueSetter !== prototypeValueSetter) {
    prototypeValueSetter.call(element, value);
  } else if (valueSetter) {
    valueSetter.call(element, value);
  } else {
    element.value = value;
  }
  element.dispatchEvent(new Event('change', { bubbles: true }));
}
